import { Markdown } from "@/components/common/markdown"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useToolTutorial } from "@/features/admin/hooks/use-tool-catalog"
import { ChevronDown } from "lucide-react"

interface ToolTutorialSheetProps {
  toolName: string | null
  toolLabel: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ToolTutorialSheet({
  toolName,
  toolLabel,
  open,
  onOpenChange,
}: ToolTutorialSheetProps) {
  const { data: tutorial, isLoading } = useToolTutorial(open ? toolName : null)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{toolLabel}</SheetTitle>
          <SheetDescription>Tutorial e perguntas frequentes</SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="space-y-3 p-4">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {!isLoading && tutorial && (
          <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
            <TabsList className="mx-4 mt-2 flex-wrap justify-start">
              <TabsTrigger value="overview">Visão geral</TabsTrigger>
              <TabsTrigger value="prompt">Como instruir</TabsTrigger>
              <TabsTrigger value="examples">Exemplos</TabsTrigger>
              <TabsTrigger value="faq">FAQ</TabsTrigger>
            </TabsList>

            <ScrollArea className="min-h-0 flex-1 px-4 pb-6">
              <TabsContent value="overview" className="mt-4">
                <Markdown>{tutorial.overview_md}</Markdown>
              </TabsContent>
              <TabsContent value="prompt" className="mt-4">
                <Markdown>{tutorial.prompt_guide_md}</Markdown>
              </TabsContent>
              <TabsContent value="examples" className="mt-4">
                <Markdown>{tutorial.examples_md}</Markdown>
              </TabsContent>
              <TabsContent value="faq" className="mt-4 space-y-2">
                {tutorial.faq.length === 0 && (
                  <p className="text-muted-foreground text-sm">Nenhuma pergunta cadastrada.</p>
                )}
                {tutorial.faq.map((item, index) => (
                  <Collapsible key={index} className="rounded-lg border">
                    <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between gap-2 p-3 text-left text-sm font-medium">
                      {item.question}
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="border-t p-3">
                      <Markdown>{item.answer_md}</Markdown>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  )
}
